import { useEffect, useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { Button } from "@/components/Button";
import { Titel } from "@/components/entwurf/Titel";
import { Wortmarke } from "@/components/Wortmarke";
import cubicMetaSymbol from "@/bilder/cubic-meta-symbol.svg";
import {
  istKonfiguriert,
  melde_an,
  sichererAnmeldeZielpfad,
  token,
} from "@/lib/auth";

const RUECKWEG_FEHLER =
  "Die Anmeldung konnte nicht abgeschlossen werden. Bitte versuche es noch einmal.";

export function Anmeldung() {
  const search = useSearch({ from: "/anmeldung" });
  const [hinweis, setzeHinweis] = useState<string | null>(() =>
    search.fehler === "rueckweg" ? RUECKWEG_FEHLER : null,
  );
  const [wirdWeitergeleitet, setzeWirdWeitergeleitet] = useState(false);
  const ziel = sichererAnmeldeZielpfad(search.von);

  useEffect(() => {
    if (token()) {
      globalThis.location.assign(ziel);
    }
  }, [ziel]);

  async function starten() {
    if (wirdWeitergeleitet) return;
    setzeHinweis(null);

    if (!istKonfiguriert()) {
      setzeHinweis(
        "Die Anmeldung ist derzeit nicht eingerichtet. Bitte wende dich an die Administration.",
      );
      return;
    }

    setzeWirdWeitergeleitet(true);
    try {
      await melde_an(ziel);
    } catch (fehler) {
      console.error("[anmeldung] Weiterleitung fehlgeschlagen", fehler);
      setzeHinweis(
        "Authentik ist gerade nicht erreichbar. Prüfe deine Verbindung und versuche es erneut.",
      );
      setzeWirdWeitergeleitet(false);
    }
  }

  return (
    <main className="anmeldung-papier min-h-dvh lg:grid lg:grid-cols-[minmax(0,1.1fr)_minmax(27rem,0.9fr)]">
      <section
        className="flex min-h-dvh items-center px-6 py-12 sm:px-10 lg:col-start-2 lg:row-start-1 lg:px-12 xl:px-20"
        aria-label="Anmeldung"
      >
        {/*
          Abstände, Größen und Positionen neu gesetzt: Der Bereich war nicht
          stimmig. Gemessen waren die Lücken 56 / 20 / 24 / 40 px: kein
          System, und das Signet mit 64px größer als der 46px-Titel, überwog ihn
          also.

          Der Rhythmus folgt jetzt dem Rest der Anwendung. Auf „Heute" steht die
          Mono-Zeile **14px** über dem Anzeigetitel, weil beide zusammengehören;
          genauso hier Wortmarke und „Moin.". Große Sprünge (44px) trennen die
          Blöcke, kleine (14/20px) binden zusammen, was eine Einheit ist:

            Signet ──44── Wortmarke ─14─ „Moin." ─20─ Satz ──44── Knopf

          Das Signet ist auf 40px zurückgenommen, damit der Titel führt und
          nicht die Marke daneben.
        */}
        <div className="mx-auto w-full max-w-[420px]">
          <img
            src={cubicMetaSymbol}
            alt="cubic meta"
            width={64}
            height={54}
            className="mb-11 block h-auto w-10"
            draggable={false}
          />

          <Wortmarke groesse={26} mitZeichen={false} className="ml-px" />

          <Titel grad={46} className="mt-3.5">
            Moin.
          </Titel>
          <p className="mt-5 text-[15px] font-light leading-[1.65] text-tinte3">
            Rein da. Gibt genug zu tun.
          </p>

          <div className="mt-11">
            {/*
              Die Kennung sitzt jetzt an der Meldung, nicht am Begrüßungssatz.
              Vorher zeigte `aria-describedby` des Knopfes auf „Rein da. Gibt
              genug zu tun." — das beschreibt den Knopf nicht, und die
              tatsächliche Fehlermeldung war mit ihm gar nicht verbunden. Ein
              Vorleseprogramm nannte also die Begrüßung und verschwieg den
              Fehler.
            */}
            {hinweis ? (
              <p
                id="anmeldung-fehler"
                className="mb-5 border-l-2 border-warnflaeche py-1 pl-4 text-sm leading-6 text-warn"
                role="alert"
              >
                {hinweis}
              </p>
            ) : null}

            {/*
              Schmaler statt kantenbündig, aber linksbündig — auf Ansage von
              Malwin. Er stand kurz mittig; links passt er zur linken Kante von
              Signet, Wortmarke, „Moin." und dem Satz darunter, statt als
              einziges Element aus der Reihe zu fallen.

              `min-w` hält die Breite über beide Beschriftungen hinweg gleich,
              sonst springt der Knopf beim Umschalten auf den Ladetext:
              „Weiterleitung wird vorbereitet" braucht mit Symbol gemessen rund
              265px.
            */}
            <Button
              className="h-11 min-w-[17rem] border-blue-600 bg-blue-600 px-6 text-[13.5px] font-medium text-white hover:border-blue-700 hover:bg-blue-700 active:border-blue-800 active:bg-blue-800 focus-visible:outline-tinte focus-visible:outline-offset-4"
              isLoading={wirdWeitergeleitet}
              loadingText="Weiterleitung wird vorbereitet"
              onClick={() => void starten()}
              aria-describedby={hinweis ? "anmeldung-fehler" : undefined}
            >
              Mit Authentik anmelden
            </Button>
          </div>
        </div>
      </section>

      <aside
        className="anmeldung-markenflaeche relative hidden min-h-dvh overflow-hidden lg:col-start-1 lg:row-start-1 lg:block"
        aria-hidden="true"
      >
        {/*
          Die Reihenfolge ist die Aussage: Leuchten soll allein der Hintergrund
          hinter dem Raben, nicht der Rabe selbst.

          Der Schein steht **vor** dem Bild und liegt damit darunter; der Rabe
          verdeckt ihn, und das Grün scheint nur um seine Silhouette herum
          durch. Vorher lag er als Teil der Aura obenauf und hellte seine Brust
          mit auf.

          Der Schleier bleibt die oberste Ebene: Er blendet die linke Hälfte ab,
          damit der Text auf dem Bild lesbar bleibt.
        */}
        <div className="anmeldung-schein absolute inset-0" />
        <div className="anmeldung-metarr absolute inset-0" />
        <div className="anmeldung-raster absolute inset-0" />
        <div className="anmeldung-schleier absolute inset-0" />

        <div className="relative z-10 flex min-h-dvh flex-col px-12 py-10 xl:px-16 xl:py-12">
          <Wortmarke
            groesse={26}
            mitZeichen={false}
            aufDunkel
            className="ml-px"
          />

          {/*
            Von `12vh` auf `9vh` — der Block saß zu tief. Bleibt bewusst in `vh`: Der Abstand soll mit der
            Fensterhöhe atmen, damit der Satz auf niedrigen Bildschirmen nicht
            in den Raben hineinläuft.
          */}
          <div className="mt-[9vh] max-w-[29rem]">
            <p className="text-[clamp(3rem,4.2vw,4.25rem)] font-extralight leading-[0.96] tracking-anzeige text-tafel-tinte">
              Klarheit
              <br />
              {" "}beginnt hier.
            </p>
            <p className="mt-7 max-w-[31ch] text-[15px] font-light leading-[1.65] text-tafel-tinte2">
              Projekte, Aufgaben, Wissen und Entscheidungen – verbunden in
              einem Arbeitsraum. Metarr behält den Kontext im Blick.
            </p>
          </div>

          <div className="mt-auto max-w-[18rem] pb-2 font-mono text-[10.5px] uppercase leading-5 tracking-[0.12em] text-tafel-tinte3">
            Projekte · Aufgaben · Wissen
            <br />
            {" "}Ein System · Ein gemeinsamer Stand
          </div>
        </div>
      </aside>
    </main>
  );
}
